import { useCurrentTenant } from "@qpr/client-library";
import { useAuth } from "@qpr/client-library/dist/esm";
import * as conf from "qpr-base/configuration";
import React, { ReactNode, ReactNodeArray, useEffect, useState } from "react";
import initializeLanguages from "./../../i18n";
import { normalizeUserProfile } from "utils/normalizeUserProfile";
import api, { ApiResult } from "./../apicaller/api-caller";
import { ApiEndpoint } from "./../apicaller/api-consts";
import { UserContext } from "./UserContext";

type UserBootstrapResult = {
  userdata: ApiResult;
  profileResult?: ApiResult;
  configPayload?: any;
};

type UserState = {
  fullname: string;
  email: string;
  username: string;
  profile?: any;
  fullName?: string;
  profileId?: string;
  [key: string]: any;
};

const userBootstrapInFlight = new Map<string, Promise<UserBootstrapResult>>();
const userBootstrapResolved = new Map<string, UserBootstrapResult>();

const extractUserPayload = (result: ApiResult | undefined): Record<string, any> | undefined => {
  if (!result) return undefined;
  if (result.data && typeof result.data === "object") {
    return result.data;
  }
  if (
    typeof result === "object" &&
    ("username" in result || "fullname" in result || "fullName" in result || "profileId" in result)
  ) {
    return result as unknown as Record<string, any>;
  }
  return undefined;
};

const mergeProfileWithSessionRoles = (
  profile: ReturnType<typeof normalizeUserProfile>,
  userPayload?: Record<string, any>
) => {
  const normalized = normalizeUserProfile(profile) || {};
  const sessionRole = String(userPayload?.role || "").trim();
  const sessionUiRole = String(userPayload?.uiRole || "").trim();
  const groups = userPayload?.groups;
  const sessionGroups = Array.isArray(groups) ? groups : normalized.meaGroups;

  return {
    ...normalized,
    role: String(normalized.role || "").trim() || sessionRole,
    uiRole: String(normalized.uiRole || "").trim() || sessionUiRole,
    meaGroups: sessionGroups,
  };
};

const toUserState = (payload: Record<string, any>, profile?: any): UserState => ({
  fullname: payload.fullname || payload.fullName || "",
  email: payload.email || "",
  username: payload.username || "",
  ...payload,
  profile: mergeProfileWithSessionRoles(profile, payload),
});

const getUserBootstrapData = async (
  tenantSymbol: string,
  isAuthenticated: boolean
): Promise<UserBootstrapResult> => {
  const cacheKey = `${tenantSymbol}:${isAuthenticated ? "auth" : "anon"}`;

  if (userBootstrapResolved.has(cacheKey)) {
    return userBootstrapResolved.get(cacheKey)!;
  }

  if (userBootstrapInFlight.has(cacheKey)) {
    return userBootstrapInFlight.get(cacheKey)!;
  }

  const request = (async () => {
    const userdata: ApiResult = await api.asyncCallApi(
      `${ApiEndpoint.sessionstatus}/${tenantSymbol}`,
      {
        method: "GET",
      }
    );

    let profileResult: ApiResult | undefined;
    let configPayload: any;

    const userPayload = extractUserPayload(userdata);

    if (userdata.ok && isAuthenticated) {
      const profileId = userPayload?.profileId;
      const configPromise = api
        .asyncCallApi(`${ApiEndpoint.config}`, { method: "GET" })
        .then((r) => r?.data)
        .catch(() => undefined);
      if (profileId) {
        const [profileRes, cfg] = await Promise.all([
          api.asyncCallApi(`${ApiEndpoint.profiles}/${profileId}`, {
            method: "GET",
          }),
          configPromise,
        ]);
        profileResult = profileRes;
        configPayload = cfg;
      } else {
        configPayload = await configPromise;
      }
    } else if (userdata.ok && userPayload?.profileId) {
      profileResult = await api.asyncCallApi(
        `${ApiEndpoint.profiles}/${userPayload.profileId}`,
        { method: "GET" }
      );
    }

    const payload = { userdata, profileResult, configPayload };
    userBootstrapResolved.set(cacheKey, payload);
    userBootstrapInFlight.delete(cacheKey);
    return payload;
  })();

  userBootstrapInFlight.set(cacheKey, request);
  return request;
};

const UserContextProvider = ({
  children,
}: {
  children: ReactNode | ReactNodeArray;
}) => {
  const tenantSymbol = useCurrentTenant().symbol;
  const [user, setUser] = useState<UserState>({
    fullname: "",
    email: "",
    username: "",
  });
  const [isLoading, setIsLoading] = useState(true);
  const { auth } = useAuth();
  useEffect(() => {
    const init = async () => {
      const { userdata, profileResult, configPayload } = await getUserBootstrapData(
        tenantSymbol,
        !!auth?.isAuthenticated
      );
      const userPayload = extractUserPayload(userdata);

      if (userdata.ok && userPayload) {
        setUser(
          toUserState(
            userPayload,
            normalizeUserProfile(profileResult?.data)
          )
        );
      }

      if (userdata.ok && auth?.isAuthenticated) {
        conf.setDefaults("languages", configPayload?.languages || ["en", "ar"]);
        conf.setViewSettings(configPayload?.views);
        initializeLanguages();
      }
      
      // Set loading to false immediately so page can render
      setIsLoading(false);

    };
    init();
  }, [tenantSymbol, auth?.isAuthenticated]);
  return isLoading ? (
    <> </>
  ) : (
    <UserContext.Provider value={user}>{children}</UserContext.Provider>
  );
};

export default UserContextProvider;
